import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  Address,
  AddressQuery,
  AddressType,
  BalanceService,
  config,
  ErrorStateService,
  MarketplaceFacade,
  MarketplaceBasketCustomFieldModel,
  MarketplaceItemExtended,
  MarketplaceV2Query,
  MemberDetails,
  MemberQuery,
  ProcessProviderId,
  PurchaseService,
  TermsAndCondtionsType,
  TransactionSuccessService,
  MarketplaceItemAttributeValue,
} from '@fgb/core';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { RegexUS } from '@fgb/portal-component-library/src/lib/shared/utilities';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, finalize, Subject, take, takeUntil } from 'rxjs';
import { CollectAddressModal } from '@fgb/portal-component-library/src/lib/loyalty/collect-address-modal';
import { PipesModule } from '@fgb/portal-component-library/src/lib/pipes';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';

@Component({
  selector: 'fgbcl-marketplace-purchase-confirmation-v2',
  standalone: true,
  imports: [FGBSharedModule, CommonModule, PipesModule, DirectivesModule, LoadingModule],
  templateUrl: './marketplace-purchase-confirmation-v2.component.html',
  styleUrl: './marketplace-purchase-confirmation-v2.component.scss',
})
export class MarketplacePurchaseConfirmationV2Component implements OnInit {
  @Input() templateSelected: string;
  @Input() marketplaceItem: MarketplaceItemExtended;
  @Input() selectedAttribute: MarketplaceItemAttributeValue;
  @Input() validCustomFields: MarketplaceBasketCustomFieldModel[];
  @Input() purchaseQuantity: number;
  @Input() addToCart: boolean;

  @Output() addToCartTermsConfirmed = new EventEmitter<boolean>();

  private _destroyed$ = new Subject<void>();
  private price: number;
  private marketplaceFacade = inject(MarketplaceFacade);
  private marketplaceQuery = inject(MarketplaceV2Query);
  private formBuilder = inject(UntypedFormBuilder);
  private purchaseService = inject(PurchaseService);
  private memberQuery = inject(MemberQuery);
  private addressQuery = inject(AddressQuery);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  private errorService = inject(ErrorStateService);
  private balanceService = inject(BalanceService);
  private transactionSuccessService = inject(TransactionSuccessService);

  config: any = config;
  addresses: Address[];
  shippingAddressForm: UntypedFormGroup;
  shippingAddress: Address;
  defaultAddress: Address;
  member: MemberDetails;
  termsLink: string;
  TermsAndCondtionsType = TermsAndCondtionsType;
  processProviderId = ProcessProviderId;
  modal = inject(NgbActiveModal);
  isLoading = this.marketplaceQuery.isLoading;

  private successUrl = 'rewards/marketplace/purchase/success/';

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnInit() {
    this.price = this.marketplaceItem.PointValue ?? 0;
    combineLatest([this.memberQuery.selectMemberDetails(), this.addressQuery.selectAddresses([AddressType.DefaultAddress])])
      .pipe(take(1))
      .subscribe(([md, addresses]) => {
        if (!md) return;

        this.member = md;
        this.addresses = addresses;

        this.defaultAddress = this._getDefaultAddress(md);
        this.shippingAddress = this._getDefaultShippingAddress();

        this.shippingAddressForm = this.initializeShippingAddressForm(this.shippingAddress);
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /* ================================
   * Public Api
   * ================================ */

  /** Open address collection modal and handle submitted address */
  openAddressCollectionModal(
    address: Address,
    orderId: string,
    saveMemberDetailsAddress: boolean,
    saveShippingAddress: boolean,
    saveOrderAddress: boolean
  ) {
    const modal = this.modalService.open(CollectAddressModal, { ariaLabelledBy: 'modal-basic-title', centered: true });
    modal.componentInstance.address = address;
    modal.componentInstance.orderId = orderId;
    modal.componentInstance.saveMemberDetailsAddress = saveMemberDetailsAddress;
    modal.componentInstance.saveShippingAddress = saveShippingAddress;
    modal.componentInstance.saveOrderAddress = saveOrderAddress;
    modal.componentInstance.submittedAddress.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this.closeModal();
      this.continueOrder(orderId);
    });
  }

  /** Open terms and conditions modal */
  openTerms(content: any) {
    this.modalService.open(content, { centered: true });
  }

  /** Finalises a purchase that was paused for address collection. Called once the user submits their address via `CollectAddressModal`. */
  continueOrder(orderId: string) {
    const cost = this.price * this.purchaseQuantity;
    this.purchaseService
      .continueOrder(orderId)
      .pipe(
        finalize(() => {
          this.marketplaceFacade.clearCache();
        })
      )
      .subscribe({
        next: (continueResponse) => {
          if (continueResponse.Data.Success) {
            this.balanceService.debitBalance(this.config.purseConfig.virtualLoyalty, cost);
            this.transactionSuccessService.setTransaction(this.marketplaceItem, this.purchaseQuantity, cost);

            this.router.navigate([this.successUrl], { queryParams: { purchaseOrderId: orderId } });
          } else {
            this.errorService.clearErrors();
            this.errorService.addError(
              !!continueResponse.Data.ErrorMessage ? continueResponse.Data.ErrorMessage : 'Purchase failed'
            );
          }
        },
      });
  }

  /** Process the purchase request */
  purchase(): void {
    if (this.isLoading()) return;
    const price = this.price;

    if (price == undefined) {
      this.errorService.clearErrors();
      this.errorService.addError('Failed to purchase item');
      return;
    }

    this.marketplaceFacade
      .purchase(
        this.marketplaceItem,
        this.purchaseQuantity,
        this.selectedAttribute?.AttributeMappingId,
        this.validCustomFields ? this.validCustomFields : [],
        this.templateSelected === 'confirmContentCardPayment' ? true : false
      )
      .pipe(
        finalize(() => {
          this.marketplaceFacade.clearCache();
        })
      )
      .subscribe({
        next: (response) => {
          if (response.Data.Success) {
            this.router.navigate([this.successUrl], { queryParams: { purchaseOrderId: response.Data.OrderId } });
            this.modal.close();
          } else if (response) {
            if (response.Data.ProviderActionInformation?.NextAction) {
              if (response.Data.ProviderActionInformation.NextAction == 20) {
                this.openAddressCollectionModal(this.shippingAddress, response.Data.OrderId, false, true, false);
              } else if (response.Data.ProviderActionInformation.NextAction == 40) {
                this.openAddressCollectionModal(this.defaultAddress, response.Data.OrderId, true, false, false);
              } else {
                this.errorService.clearErrors();
                this.errorService.addError(!!response.Data.ErrorMessage ? response.Data.ErrorMessage : 'Purchase failed');
                this.modal.close();
              }
            }
          }
        },
        error: (error) => {
          this.modal.close();
        },
      });
  }

  closeModal() {
    this.modalService.dismissAll();
  }

  /* ================================
   * Private Helpers
   * ================================ */

  /** Initialize shipping address form */
  private initializeShippingAddressForm(address: Address) {
    return this.formBuilder.group({
      addressName: [address.AddressName, Validators.required],
      emailAddress: [address.EmailAddress, Validators.email],
      street: [address.Street, Validators.required],
      town: [address.Town, Validators.required],
      county: [address.County, Validators.required],
      country: [address.Country, Validators.required],
      postcode: [address.Postcode, [Validators.required, Validators.pattern(RegexUS.postCode)]],
      firstName: [address.FirstName, Validators.required],
      surname: [address.Surname, Validators.required],
      homeNumber: [address.HomeNumber, Validators.required],
      portalId: [address.PortalId],
      clubId: [address.ClubId],
    });
  }

  /** Get default address for member */
  private _getDefaultAddress(member: MemberDetails): Address {
    return {
      FirstName: member.FirstName,
      Surname: member.Surname,
      Street: member.Street,
      Town: member.Town,
      County: member.County,
      Postcode: member.PostCode,
      Country: member.Country,
      HomeNumber: member.HomeNumber,
      EmailAddress: member.EmailAddress1,
      PortalId: member.PortalId,
      ClubId: member.ClubId,
      AddressName: 'Default Address',
      AddressType: AddressType.DefaultAddress,
    } as Address;
  }

  /** Get default shipping address for member */
  private _getDefaultShippingAddress(): Address {
    const shippingAddress =
      this.addresses?.find((a) => a.AddressType === AddressType.ShippingAddress1) ??
      this.addresses?.find((a) => a.AddressType === AddressType.ShippingAddress2);

    return (
      shippingAddress ??
      ({
        FirstName: '',
        Surname: '',
        Street: '',
        Town: '',
        County: '',
        Postcode: '',
        Country: '',
        HomeNumber: '',
        EmailAddress: '',
        PortalId: this.member.PortalId,
        ClubId: this.member.ClubId,
        AddressType: AddressType.ShippingAddress1,
        AddressName: 'Shipping Address',
      } as Address)
    );
  }
}
